import { useSelector } from "react-redux";
import { getModule, getShowLkBadge, getTenantIsInternal } from "store/userSlice";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { empty, formatDateTime, formatFixedDigits, formatMoney, removeTrailingZeros } from "helpers/common";
import sanitize from "helpers/sanitize";
import { getInterviewsSettings } from "store/interviewsSettingsSlice";
import TextTruncate from "components/ui/TextTruncate";
import { useTranslation } from "translation/useTranslation";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getGeneralSettings } from "store/generalSettingsSlice";
import { isDbModule } from "helpers/module";

function BalancedScorecardFieldValue({ employee, bs, field, classNames = '', forEmployeeManagement = false, forTable = true, displayLkTag = false, useInterviewIcon = false, softwareSettings = {}, resizedCount = null, useCalculationPayFactor = false }) {

  const { translate, editableTranslate } = useTranslation();

  const location = useLocation();

  const generalSettings = useSelector(getGeneralSettings);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const isSecondaryEvaluator = useMemo(() => {
    return location.pathname === '/evaluation/secondary-evaluator-overview';
  }, [location]);

  const resultAsNumeric = useMemo(() => {
    const customConfig = bs?.balanced_scorecard_setting?.custom_config;

    if (!generalSettings && !customConfig) return true;

    if (customConfig?.result_format === 'numeric') return true;

    // generalSettings is an array of object, search the key 'balanced_scorecard_result_format'
    const setting = generalSettings?.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }

    return false;
  }, [generalSettings, bs]);

  const showLkBadge = useSelector(getShowLkBadge);

  const isInternalTenant = useSelector(getTenantIsInternal);

  const interviews = useSelector(getInterviewsSettings);

  const complaintStatus = useMemo(() => {
    if (bs) {
      const list = bs.complaints?.data ?? [];
      const total = list.length;
      const resolved = list.filter(c => c.is_resolved).length;
      if (total === 0) return 'no_complaint';
      else if (resolved === 0) return 'unresolved';
      else if (total === resolved) return 'resolved';
      else if (resolved < total) return 'partially_resolved';
    }
    return null;
  }, [bs]);

  const hasIncompleteInterview = useMemo(() => {
    if (bs?.balanced_scorecard_setting?.custom_config?.interview_enabled === false) return false;

    if (bs.interviews) {
      for (const k in bs.interviews) {
        if (['overdue', 'incomplete', 'in_progress'].includes(bs.interviews[k])) return true;
      }
    }
    return false;
  }, [bs]);

  let val = null;
  let divClass = '';

  if (field.alignment == 'center') {
    divClass = 'justify-content-center';
  } else if (field.alignment == 'right') {
    divClass = 'justify-content-end';
  } else {
    divClass = 'justify-content-start';
  }

  if (field.slug === 'balanced_scorecard_template') {
    let bsName = bs.balanced_scorecard_setting?.name || '';
    bsName = bsName.trim();

    const data = <>
      <span data-tooltip-id={`bs-${bs.hash_id}-tooltip`}>
        {bsName}
      </span>
      {
        (showLkBadge && isInternalTenant && (forEmployeeManagement || displayLkTag)) && <>
          &nbsp;
          <span className={`badge badge-pill badge-${bs.balanced_scorecard_setting?.tag?.color}`} data-tooltip-id={`bs-${bs.balanced_scorecard_setting?.tag?.translation_key}-tooltip`}>
            {editableTranslate(bs.balanced_scorecard_setting?.tag?.translation_key, bs.balanced_scorecard_setting?.tag?.tag)}
          </span>
        </>
      }
      {
        (forEmployeeManagement) && <ReactTooltip id={`bs-${bs.hash_id}-tooltip`} place="bottom" content={bs.balanced_scorecard_setting?.name || ''} />
      }
    </>;

    if (!forTable) return data;

    let slug = bs?.balanced_scorecard_setting?.slug;
    let fullNameTooltip = null;

    if (isDbModuleActive) {
      if (slug?.endsWith('-regular')) {
        fullNameTooltip = bsName + ` (${bs?.balanced_scorecard_setting?.description})`;
        bsName += ' 👤';
      } else if (slug?.endsWith('-leadership')) {
        fullNameTooltip = bsName + ` (${bs?.balanced_scorecard_setting?.description})`;
        bsName += ' 👔';
      }
    }

    return <div className={divClass + ' employee-lk-text'}>
      <br />
      <TextTruncate text={bsName} className="d-inline-block v-middle" resizedCount={resizedCount} tooltip={fullNameTooltip} alwaysShowTooltip={fullNameTooltip ? true : false} />
      {
        (showLkBadge && isInternalTenant && (forEmployeeManagement || displayLkTag)) && <>
          &nbsp;
          <span className={`badge badge-pill badge-${bs.balanced_scorecard_setting?.tag?.color}`} data-tooltip-id={`bs-${bs.balanced_scorecard_setting?.tag?.translation_key}-tooltip`}>
            {editableTranslate(bs.balanced_scorecard_setting?.tag?.translation_key, bs.balanced_scorecard_setting?.tag?.tag)}
          </span>
        </>
      }
    </div>;
  } else if (field.slug === 'budget') {
    val = bs.budget_setting?.name || '';
    if (val === '') {
      return <div className={divClass + ' employee-lk-text'}>
        <i className="bi bi-exclamation-triangle-fill orange-color" data-testid="bs-no-budget-warning" data-tooltip-id="no-budget-tooltip"></i>
      </div>;
    }
  } else if (field.slug === 'group') {
    let groupName = bs.group?.name || '';
    let groupRoleIcon = '';

    if (groupName === '') {
      return <div className={divClass + ' employee-lk-text'}>
        <i className="bi bi-exclamation-triangle-fill orange-color" data-testid="bs-no-group-warning" data-tooltip-id={isDbModuleActive ? 'no-group-db-tooltip' : 'no-group-tooltip'}></i>
      </div>;
    }

    if (forEmployeeManagement) {
      if (bs.group?.roles.evaluator || (isDbModuleActive && bs.group?.roles?.secondary_evaluator)) {
        groupRoleIcon += '<i class="bi bi-clipboard-check ml-1" data-tooltip-id="evaluator-tooltip"></i>';
      }

      if (!isDbModuleActive && bs.group?.roles.reviewer) {
        groupRoleIcon += '<i class="bi bi-eye ml-1" data-tooltip-id="reviewer-tooltip"></i>';
      }
    }

    if (!forTable) return <span>
      {groupName} <span dangerouslySetInnerHTML={{ __html: sanitize(groupRoleIcon) }}></span>
    </span>;

    return <div className={divClass + ' employee-lk-text'}>
      <TextTruncate text={groupName} className="d-inline-block v-middle" resizedCount={resizedCount} /> <span className="v-middle" dangerouslySetInnerHTML={{ __html: sanitize(groupRoleIcon) }}></span>
    </div>;
  } else if (field.slug === 'lk') {
    let lkClass = 'bi-x-circle-fill';
    let lkTooltip = translate('tooltips.evaluation.lk.default_tooltip');

    const systematicObjectives = bs.agreement_data?.systematicObjectives?.filter(so => ('' + so.agreed) === 'true' || so?.sub?.filter(s => ('' + s.agreed) === 'true').length > 0);

    if (employee.exclude_from_payout) { // Employee is excluded from payout
      lkClass = 'bi-arrow-down-circle-fill green-color';
      lkTooltip = translate('tooltips.evaluation.lk.exclusion_tooltip').replace('$X', employee?.exclude_from_payout_reason);
    } else if (bs.evaluation_completed_at) { // Balance score card closed
      lkClass = 'bi-check-circle-fill';
      lkTooltip = translate(isDbModuleActive ? 'state_employee.rating_card_completed' : 'tooltips.evaluation.lk.completed_tooltip') + ' (' + formatDateTime(bs.evaluation_completed_at) + ')';
    } else if (bs?.initial_evaluation_completed_at) { // state employee, completed by first evaluator
      lkClass = 'bi-check-circle orange-color';
      lkTooltip = translate('state_employee.initial_evaluation_completed') + ' (' + formatDateTime(bs.initial_evaluation_completed_at) + ')';
    } else if (bs.rating_mode === 'average_value_percentile' || bs.alternative_result?.includes('average_value')) { // Employee gets the average value
      lkClass = 'bi-slash-circle-fill green-color';
      lkTooltip = translate('tooltips.evaluation.lk.average_tooltip');
    } else if (bs.agreement_data?.feedback || systematicObjectives?.length > 0 || bs.individual_objectives?.length > 0 || bs.team_objectives?.length > 0) { // Any feedback/objective is stored
      lkClass = 'bi-exclamation-circle-fill orange-color';
      lkTooltip = translate(isDbModuleActive ? 'state_employee.rating_card_not_completed' : 'tooltips.evaluation.lk.not_completed_tooltip');
    }

    let showInterviewRequiredIcon = false;
    if (hasIncompleteInterview) {
      if (!isSecondaryEvaluator) {
        if (bs?.state_current_evaluation_step === 'primary_meeting') showInterviewRequiredIcon = true;
      } else {
        if (bs?.state_current_evaluation_step === 'secondary_meeting') showInterviewRequiredIcon = true;
      }
    }

    const nextStep = bs?.state_current_evaluation_step || null;

    const data = <span>
      <i className={`bi ${lkClass}`} data-tooltip-id={`lk-${bs.hash_id}-tooltip`}></i>
      <ReactTooltip id={`lk-${bs.hash_id}-tooltip`} place="bottom">
        {lkTooltip}
        {
          nextStep && <>
            <br /><br />
            {translate('state_employee.evaluation_flow.next_step') + ': ' + translate('state_employee.evaluation_flow.step_' + nextStep)}
          </>
        }
      </ReactTooltip>

      {
        useInterviewIcon && <i className={`bi bi-exclamation-triangle interview-alert-icon ${showInterviewRequiredIcon ? '' : 'invisible'}`} data-tooltip-id="state-interview-required-tooltip"></i>
      }
    </span>;

    if (!forTable) return data;
    return <div className="employee-lk-text justify-content-center">{data}</div>;
  } else if (field.slug === 'personal_interviews') {
    if (['unresolved', 'partially_resolved'].includes(complaintStatus)) {
      divClass += ' warning-cell box-cell';
    } else if (complaintStatus === 'resolved') {
      divClass += ' success-cell box-cell';
    }

    return <span>
      <div className={divClass} data-tooltip-id={bs.interviews.length <= 0 && classNames.includes('box-cell') ? 'no-interview-tooltip' : ''}>
        {
          bs.interviews && Object.keys(bs.interviews).map((key, i) => {
            if (bs.interviews[key] === 'not_applicable') {
              return '';
            }

            if (bs.interviews[key] === 'overdue') {
              return <i key={i} className="bi bi-x-circle-fill mr-1"></i>
            } else if (bs.interviews[key] === 'incomplete') {
              return <i key={i} className="bi bi-circle mr-1"></i>
            } else if (bs.interviews[key] === 'completed') {
              return <i key={i} className="bi bi-check-circle-fill mr-1"></i>
            } else if (bs.interviews[key] === 'in_progress') {
              return <i key={i} className="bi bi-check-circle-fill text-warning mr-1"></i>
            }

            return null;
          })
        }
        {
          (bs.interviews.length <= 0 && !classNames.includes('box-cell')) && <i className="bi bi-info-circle mr-1" data-tooltip-id="no-interview-tooltip"></i>
        }
        {
          (bs.interviews.length <= 0 && classNames.includes('box-cell') && interviews) && interviews.map((_, i) => <i key={i} className="bi bi-circle invisible mr-1" />)
        }
        <i className={`bi bi-exclamation-triangle-fill ${(['unresolved', 'partially_resolved'].includes(complaintStatus) ? 'text-warning' : (complaintStatus === 'resolved' ? 'text-success' : ''))} ${(complaintStatus !== 'no_complaint') ? '' : 'invisible'} ml-3`}></i>
      </div>
      {
        forTable && <br />
      }
    </span>;
  } else if (['pay_factor'].includes(field.slug)) {
    const payFactor = useCalculationPayFactor ? (bs?.calculation_pay_factor ?? bs[field.slug]) : bs[field.slug];
    val = (payFactor ? formatMoney(payFactor) : '');
  } else if (field.slug === 'result') {
    val = '';
    if (isDbModuleActive) {
      const firstEvaluator = bs?.agreement_data?.overall_result?.first_evaluator;
      const firstEvaluatorResult = (resultAsNumeric ? removeTrailingZeros(firstEvaluator?.value) : firstEvaluator?.level);
      const secondEvaluator = bs?.agreement_data?.overall_result?.second_evaluator;
      let className = null;
      let tooltip = null

      if (!isSecondaryEvaluator && !bs?.initial_evaluation_completed_at && !bs.is_evaluation_completed) {
        val = '';
      } else {
        if (bs.is_evaluation_completed && firstEvaluator?.value === secondEvaluator?.value) {
          val = removeTrailingZeros(bs.result_formatted);
          className = 'text-success font-weight-bold';
          tooltip = translate('tooltips.evaluation.state_both_evaluators_agree');
        } else if (bs.is_evaluation_completed && empty(secondEvaluator?.value)) {
          val = removeTrailingZeros(bs.result_formatted);
          className = 'text-success font-weight-bold';
        } else if (bs.is_evaluation_completed && firstEvaluator?.value !== secondEvaluator?.value) {
          val = removeTrailingZeros(bs.result_formatted);
          className = 'text-default-red font-weight-bold';
          tooltip = translate('tooltips.evaluation.state_rating_overwritten').replace('$X', firstEvaluatorResult);
        } else if (bs?.initial_evaluation_completed_at) {
          val = firstEvaluatorResult;
          tooltip = translate('tooltips.evaluation.state_second_evaluator_has_not_yet_rated');
        }
      }

      if (bs?.balanced_scorecard_setting?.slug?.includes('beurteilungsbeitrag')) {
        val = '';
        className = null;
        tooltip = null;
      }

      return <div className={divClass + ' employee-lk-text'}>
        <span className={className} data-tooltip-id={tooltip ? `lk-${bs.hash_id}-result-tooltip` : ''}>
          {val}
        </span>
        {
          tooltip && <ReactTooltip id={`lk-${bs.hash_id}-result-tooltip`} place="bottom" content={tooltip} />
        }
      </div>;
    } else {
      if (bs.result && bs.is_evaluation_completed) val = removeTrailingZeros(bs.result_formatted);
    }
  } else if (field.slug === 'trend') {
    if (softwareSettings?.evaluation_trend_calculation_method === 'no_calculation') {
      val = '';
    } else if (bs.trend_calculation_type === 'below_threshold') {
      const data = <span>
        <i className={`bi bi-question-circle-fill text-blue`} data-tooltip-id={`lk-${bs.hash_id}-trend-tooltip`}></i>
        <ReactTooltip id={`lk-${bs.hash_id}-trend-tooltip`} place="bottom" content={translate('tooltips.evaluation.trend_below_threshold', '', { 'trend_calculation_method': `${translate('general_settings.trend_calculation.evaluation_trend_calculation_method.' + softwareSettings?.evaluation_trend_calculation_method, softwareSettings?.evaluation_trend_calculation_method)}` })} />
      </span>;

      if (!forTable) return data;
      return <div className="py-2">{data}</div>;
    } else if (bs.trend_calculation_type !== 'no_calculation') {
      if (softwareSettings?.evaluation_trend_display_type === 'average_value' && bs.trend_average_score) {
        val = formatFixedDigits(bs.trend_average_score, field.decimal, field.fill_up);
      } else if (softwareSettings?.evaluation_trend_display_type === 'deviation' && bs.trend_deviation) {
        val = formatFixedDigits(bs.trend_deviation, field.decimal, field.fill_up);
      } else {
        val = '';
      }
    } else {
      val = '';
    }
  } else if (['fixed_payout'].includes(field.slug)) {
    val = (bs[field.slug] ? '€ ' + formatMoney(bs[field.slug]) : '-');
  } else if (field.slug === 'weighting') {
    val = (bs.distribution_percentage || '0') + '%';
  } else {
    val = bs[field.slug] || '-';
  }

  const data = <span>{val}</span>;

  if (!forTable) return data;
  return <div className={divClass + ' employee-lk-text'}>
    <TextTruncate text={val} resizedCount={resizedCount} />
  </div>;
}

export { BalancedScorecardFieldValue };
